import { useMemo } from "react";
import {
  Area,
  AreaChart,
  CartesianGrid,
  ResponsiveContainer,
  Tooltip,
  XAxis,
  YAxis,
} from "recharts";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { ChartConfig, ChartContainer, ChartTooltipContent } from "@/components/ui/chart";

interface TrendDataPoint {
  week: string;
  value: number;
  target_min?: number | null;
  target_max?: number | null;
}

interface KPITrendChartProps {
  title: string;
  unit: string;
  data: TrendDataPoint[];
  minBand?: number | null;
  maxBand?: number | null;
}

const chartConfig = {
  value: {
    label: "Value",
    color: "hsl(var(--primary))",
  },
  target_min: {
    label: "Min Target",
    color: "hsl(142.1 76.2% 36.3%)",
  },
  target_max: {
    label: "Max Target",
    color: "hsl(0 84.2% 60.2%)",
  },
} satisfies ChartConfig;

export function KPITrendChart({ title, unit, data, minBand, maxBand }: KPITrendChartProps) {
  const formattedData = useMemo(() => {
    return data.map((point) => ({
      ...point,
      weekLabel: new Date(point.week).toLocaleDateString("en-US", {
        month: "short",
        day: "numeric",
      }),
      target_min: minBand,
      target_max: maxBand,
    }));
  }, [data, minBand, maxBand]);

  const formatValue = (value: number) => {
    if (unit === "₹") {
      if (value >= 10000000) return `${(value / 10000000).toFixed(1)}Cr`;
      if (value >= 100000) return `${(value / 100000).toFixed(1)}L`;
      if (value >= 1000) return `${(value / 1000).toFixed(1)}K`;
      return value.toFixed(0);
    }
    if (unit === "%") return `${value.toFixed(1)}%`;
    return value.toFixed(0);
  };

  if (data.length === 0) {
    return (
      <Card>
        <CardHeader>
          <CardTitle className="text-lg">{title}</CardTitle>
          <CardDescription>12-week trend</CardDescription>
        </CardHeader>
        <CardContent className="flex items-center justify-center h-[200px] text-muted-foreground">
          No data available
        </CardContent>
      </Card>
    );
  }

  return (
    <Card>
      <CardHeader className="pb-2">
        <CardTitle className="text-lg">{title}</CardTitle>
        <CardDescription>12-week trend ({unit})</CardDescription>
      </CardHeader>
      <CardContent>
        <ChartContainer config={chartConfig} className="h-[200px] w-full">
          <ResponsiveContainer width="100%" height="100%">
            <AreaChart data={formattedData} margin={{ top: 10, right: 10, left: 0, bottom: 0 }}>
              <defs>
                <linearGradient id="valueGradient" x1="0" y1="0" x2="0" y2="1">
                  <stop offset="0%" stopColor="hsl(var(--primary))" stopOpacity={0.3} />
                  <stop offset="100%" stopColor="hsl(var(--primary))" stopOpacity={0} />
                </linearGradient>
              </defs>
              <CartesianGrid strokeDasharray="3 3" className="stroke-muted" />
              <XAxis
                dataKey="weekLabel"
                tick={{ fontSize: 10 }}
                tickLine={false}
                axisLine={false}
                className="text-muted-foreground"
              />
              <YAxis
                tick={{ fontSize: 10 }}
                tickLine={false}
                axisLine={false}
                tickFormatter={formatValue}
                className="text-muted-foreground"
                width={50}
              />
              <Tooltip
                content={<ChartTooltipContent />}
                formatter={(value: number) => [formatValue(value), title]}
              />
              {minBand && (
                <Area
                  type="monotone"
                  dataKey="target_min"
                  stroke="hsl(142.1 76.2% 36.3%)"
                  strokeDasharray="4 4"
                  strokeWidth={1}
                  fill="none"
                  dot={false}
                />
              )}
              {maxBand && (
                <Area
                  type="monotone"
                  dataKey="target_max"
                  stroke="hsl(0 84.2% 60.2%)"
                  strokeDasharray="4 4"
                  strokeWidth={1}
                  fill="none"
                  dot={false}
                />
              )}
              <Area
                type="monotone"
                dataKey="value"
                stroke="hsl(var(--primary))"
                strokeWidth={2}
                fill="url(#valueGradient)"
                dot={{ fill: "hsl(var(--primary))", strokeWidth: 0, r: 3 }}
                activeDot={{ r: 5, fill: "hsl(var(--primary))" }}
              />
            </AreaChart>
          </ResponsiveContainer>
        </ChartContainer>
      </CardContent>
    </Card>
  );
}
